import React, { createContext, useContext, useState, useRef, useCallback, useMemo } from 'react';

const HistoryDrawerContext = createContext();

export function HistoryDrawerProvider({ children }) {
  const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false);
  const [openAssignments, setOpenAssignments] = useState([]);
  const [cachedLocation, setCachedLocation] = useState(null);
  const [pendingMatchEvents, setPendingMatchEvents] = useState([]);
  const lastAssignmentFetch = useRef(0);
  const locationFetchPromise = useRef(null);

  const openHistoryDrawer = useCallback(() => setHistoryDrawerOpen(true), []);
  const closeHistoryDrawer = useCallback(() => setHistoryDrawerOpen(false), []);

  const updateOpenAssignments = useCallback((assignments) => {
    setOpenAssignments(assignments);
    lastAssignmentFetch.current = Date.now();
  }, []);

  const isCacheValid = useCallback(() => {
    return Date.now() - lastAssignmentFetch.current < 30000; // 30 second cache
  }, []);

  const getCachedLocation = useCallback(() => cachedLocation, [cachedLocation]);
  const updateCachedLocation = useCallback((location) => setCachedLocation(location), []);
  const getLocationPromise = useCallback(() => locationFetchPromise.current, []);
  const setLocationPromise = useCallback((promise) => { locationFetchPromise.current = promise; }, []);

  // Add optimistic match event
  const addPendingMatchEvent = useCallback((matchData) => {
    // If already a complete event (like group_match), just add it directly
    if (matchData.eventType === 'group_match') {
      setPendingMatchEvents(prev => [matchData, ...prev]);
      return matchData.eventId;
    }

    // Determine eventType based on matchType
    // matchType: 'known' | 'soi' | 'associated' | 'associated_new'
    let eventType = 'match';
    if (matchData.matchType === 'soi') {
      eventType = 'soi_match';
    }

    // Otherwise, construct a pending match event
    const tempEvent = {
      eventId: `temp-${Date.now()}-${Math.random()}`,
      eventType,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      description: `Matched! ${matchData.personName}`,
      metadata: {
        personName: matchData.personName,
        personImage: matchData.personImage,
        matchSource: matchData.matchSource,
        matchAccuracy: matchData.matchAccuracy,
        matchType: matchData.matchType,
        person: matchData.person,
        openAssignment: matchData.openAssignment,
        capturedImageUrl: matchData.capturedImageUrl,
        boundingBox: matchData.boundingBox,
        matchCount: matchData.matchCount,
        detectedFaceCount: matchData.detectedFaceCount,
        groupEventId: matchData.groupEventId,
        assignmentEventId: matchData.openAssignment?.eventId,
        assignmentDescription: matchData.openAssignment?.description,
        statementCount: matchData.statementCount
      },
      location: matchData.location,
      _optimistic: true
    };
    setPendingMatchEvents(prev => [tempEvent, ...prev]);
    return tempEvent.eventId;
  }, []);

  // Remove pending match (after real event loaded)
  const removePendingMatchEvent = useCallback((tempId) => {
    setPendingMatchEvents(prev => prev.filter(e => e.eventId !== tempId));
  }, []);

  // Memoize value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    historyDrawerOpen,
    openHistoryDrawer,
    closeHistoryDrawer,
    openAssignments,
    updateOpenAssignments,
    isCacheValid,
    getCachedLocation,
    updateCachedLocation,
    getLocationPromise,
    setLocationPromise,
    pendingMatchEvents,
    addPendingMatchEvent,
    removePendingMatchEvent
  }), [
    historyDrawerOpen,
    openHistoryDrawer,
    closeHistoryDrawer,
    openAssignments,
    updateOpenAssignments,
    isCacheValid,
    getCachedLocation,
    updateCachedLocation,
    getLocationPromise,
    setLocationPromise,
    pendingMatchEvents,
    addPendingMatchEvent,
    removePendingMatchEvent
  ]);

  return (
    <HistoryDrawerContext.Provider value={value}>
      {children}
    </HistoryDrawerContext.Provider>
  );
}

export function useHistoryDrawer() {
  const context = useContext(HistoryDrawerContext);
  if (!context) {
    throw new Error('useHistoryDrawer must be used within HistoryDrawerProvider');
  }
  return context;
}
